import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Volume2, VolumeX, Type } from 'lucide-react';
import { Toaster, toast } from 'sonner';

const API_BASE =
  window.location.hostname === 'localhost'
    ? 'http://localhost:8081/api/v1'
    : '/api/v1';

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('qrhost_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const FONT_OPTIONS = ['Inter', 'Roboto', 'Poppins', 'Montserrat'];

export function BusinessSetup() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState<{ business_id: string; role: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: '',
    logo_url: '',
    primary_color: '#2563EB',
    secondary_color: '#1E40AF',
    background_color: '#0f172a',
    text_color: '#f8fafc',
    accent_color: '#3b82f6',
    font_family: 'Inter',
    notification_sound: true,
    custom_ready_message: '¡Tu mesa está lista!',
  });

  useEffect(() => {
    const token = localStorage.getItem('qrhost_token');
    if (!token) {
      navigate('/login');
      return;
    }
    try {
      const s = JSON.parse(localStorage.getItem('qrhost_staff') || '{}');
      setStaff(s);
      if (s.role !== 'admin') {
        navigate('/dashboard');
        return;
      }
      // Load existing business data
      fetch(`${API_BASE}/businesses/${s.business_id}`, {
        headers: getAuthHeaders(),
      })
        .then((res) => res.json())
        .then((data) => {
          setForm({
            name: data.name || '',
            logo_url: data.logo_url || '',
            primary_color: data.primary_color || '#2563EB',
            secondary_color: data.secondary_color || '#1E40AF',
            background_color: data.background_color || '#0f172a',
            text_color: data.text_color || '#f8fafc',
            accent_color: data.accent_color || '#3b82f6',
            font_family: data.font_family || 'Inter',
            notification_sound: data.notification_sound !== false,
            custom_ready_message: data.custom_ready_message || '¡Tu mesa está lista!',
          });
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    } catch {
      navigate('/login');
    }
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!staff?.business_id) return;
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/businesses/${staff.business_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          name: form.name || undefined,
          logo_url: form.logo_url || undefined,
          primary_color: form.primary_color || undefined,
          secondary_color: form.secondary_color || undefined,
          background_color: form.background_color || undefined,
          text_color: form.text_color || undefined,
          accent_color: form.accent_color || undefined,
          font_family: form.font_family || undefined,
          notification_sound: form.notification_sound,
          custom_ready_message: form.custom_ready_message || undefined,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Error al guardar');
      }
      toast.success('Guardado');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error');
    } finally {
      setSaving(false);
    }
  };

  const previewStyle = {
    '--qr-primary': form.primary_color,
    '--qr-secondary': form.secondary_color,
    '--qr-bg': form.background_color,
    '--qr-surface': lighten(form.background_color, 5),
    '--qr-text': form.text_color,
    '--qr-accent': form.accent_color,
  } as React.CSSProperties;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ background: 'var(--qr-bg)' }}>
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-xl animate-skeleton" style={{ background: 'var(--qr-surface)' }} />
          <div className="w-32 h-4 rounded animate-skeleton" style={{ background: 'var(--qr-surface)' }} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen" style={{ background: 'var(--qr-bg)', color: 'var(--qr-text)' }}>
      <Toaster position="top-right" richColors />
      <header
        className="flex items-center gap-3 px-4 py-3"
        style={{ background: 'var(--qr-surface)', borderBottom: '1px solid var(--qr-border)' }}
      >
        <button
          onClick={() => navigate('/dashboard')}
          className="p-2 rounded-lg transition-transform active:scale-95"
          style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)' }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="text-lg font-bold">Configuración del negocio</h1>
      </header>

      <main className="flex flex-col lg:flex-row gap-6 p-4 max-w-6xl mx-auto w-full">
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col gap-4 animate-fade-in">
          <div
            className="flex flex-col gap-4 p-5 rounded-2xl"
            style={{ background: 'var(--qr-surface)', border: '1px solid var(--qr-border)' }}
          >
            <h2 className="text-base font-bold uppercase tracking-wider" style={{ color: 'var(--qr-text-secondary)' }}>
              Identidad
            </h2>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" style={{ color: 'var(--qr-text-secondary)' }}>Nombre del negocio</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl outline-none"
                style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)', color: 'var(--qr-text)' }}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" style={{ color: 'var(--qr-text-secondary)' }}>URL del logo</label>
              <input
                type="url"
                value={form.logo_url}
                onChange={(e) => setForm((f) => ({ ...f, logo_url: e.target.value }))}
                placeholder="https://..."
                className="w-full px-4 py-3 rounded-xl outline-none"
                style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)', color: 'var(--qr-text)' }}
              />
            </div>
          </div>

          <div
            className="flex flex-col gap-4 p-5 rounded-2xl"
            style={{ background: 'var(--qr-surface)', border: '1px solid var(--qr-border)' }}
          >
            <h2 className="text-base font-bold uppercase tracking-wider" style={{ color: 'var(--qr-text-secondary)' }}>
              Colores
            </h2>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'primary_color', label: 'Primario' },
                { key: 'secondary_color', label: 'Secundario' },
                { key: 'background_color', label: 'Fondo' },
                { key: 'text_color', label: 'Texto' },
                { key: 'accent_color', label: 'Acento' },
              ].map((c) => (
                <div key={c.key} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: 'var(--qr-bg)' }}>
                  <input
                    type="color"
                    value={form[c.key as keyof typeof form] as string}
                    onChange={(e) => setForm((f) => ({ ...f, [c.key]: e.target.value }))}
                    className="w-10 h-10 rounded-lg border-0 p-0 cursor-pointer"
                  />
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">{c.label}</span>
                    <span className="text-xs font-mono" style={{ color: 'var(--qr-text-secondary)' }}>
                      {form[c.key as keyof typeof form] as string}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div
            className="flex flex-col gap-4 p-5 rounded-2xl"
            style={{ background: 'var(--qr-surface)', border: '1px solid var(--qr-border)' }}
          >
            <h2 className="text-base font-bold uppercase tracking-wider" style={{ color: 'var(--qr-text-secondary)' }}>
              Tipografía
            </h2>
            <div className="flex items-center gap-3">
              <Type size={18} style={{ color: 'var(--qr-text-secondary)' }} />
              <select
                value={form.font_family}
                onChange={(e) => setForm((f) => ({ ...f, font_family: e.target.value }))}
                className="flex-1 px-4 py-3 rounded-xl outline-none"
                style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)', color: 'var(--qr-text)' }}
              >
                {FONT_OPTIONS.map((font) => (
                  <option key={font} value={font}>{font}</option>
                ))}
              </select>
            </div>
          </div>

          <div
            className="flex flex-col gap-4 p-5 rounded-2xl"
            style={{ background: 'var(--qr-surface)', border: '1px solid var(--qr-border)' }}
          >
            <h2 className="text-base font-bold uppercase tracking-wider" style={{ color: 'var(--qr-text-secondary)' }}>
              Notificaciones
            </h2>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium" style={{ color: 'var(--qr-text-secondary)' }}>
                Mensaje cuando la mesa está lista
              </label>
              <input
                type="text"
                value={form.custom_ready_message}
                maxLength={100}
                onChange={(e) => setForm((f) => ({ ...f, custom_ready_message: e.target.value }))}
                className="w-full px-4 py-3 rounded-xl outline-none"
                style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)', color: 'var(--qr-text)' }}
              />
              <div className="text-right text-xs" style={{ color: 'var(--qr-text-secondary)' }}>
                {form.custom_ready_message.length}/100
              </div>
            </div>
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, notification_sound: !f.notification_sound }))}
              className="flex items-center gap-3 px-4 py-3 rounded-xl w-fit transition-transform active:scale-[0.98]"
              style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)' }}
            >
              {form.notification_sound ? <Volume2 size={18} /> : <VolumeX size={18} />}
              <span className="text-sm">Sonido {form.notification_sound ? 'activado' : 'desactivado'}</span>
            </button>
          </div>

          <button
            type="submit"
            disabled={saving}
            className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold transition-transform duration-200 active:scale-[0.98] disabled:opacity-60"
            style={{ background: 'var(--qr-primary)', color: '#fff' }}
          >
            <Save size={18} />
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </form>

        <div className="flex-1 flex flex-col gap-3 animate-fade-in">
          <h2 className="text-base font-bold uppercase tracking-wider" style={{ color: 'var(--qr-text-secondary)' }}>
            Vista previa
          </h2>
          <div
            className="flex flex-col items-center gap-4 p-6 rounded-2xl flex-1 min-h-[400px]"
            style={{
              ...previewStyle,
              background: 'var(--qr-bg)',
              border: '1px solid var(--qr-border)',
            }}
          >
            {form.logo_url && (
              <img src={form.logo_url} alt="logo" className="h-10 object-contain" />
            )}
            <div
              className="flex flex-col items-center gap-3 p-6 rounded-xl w-full max-w-xs"
              style={{ background: 'var(--qr-surface)', border: '1px solid var(--qr-border)' }}
            >
              <span className="font-mono font-extrabold text-5xl" style={{ color: 'var(--qr-text)' }}>
                ABC123
              </span>
              <div className="w-32 h-32 rounded-lg" style={{ background: 'var(--qr-bg)', border: '1px solid var(--qr-border)' }} />
              <p className="text-lg font-medium" style={{ color: 'var(--qr-primary)' }}>
                Nº 3 en cola
              </p>
              <p className="text-sm" style={{ color: 'var(--qr-text-secondary)' }}>
                ~15 min
              </p>
            </div>
            <div
              className="w-full max-w-xs p-4 rounded-xl text-center"
              style={{ background: 'var(--qr-success)', color: '#fff' }}
            >
              <p className="font-bold">{form.custom_ready_message}</p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function lighten(hex: string, percent: number): string {
  try {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, (num >> 16) + amt);
    const G = Math.min(255, ((num >> 8) & 0x00ff) + amt);
    const B = Math.min(255, (num & 0x0000ff) + amt);
    return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
  } catch {
    return '#1e293b';
  }
}
